import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { AlertTriangle, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../../store';
import { usePdfTab } from '../../hooks/usePdfTab';
import {
  getSimulatedOrderFaxPdf,
  listSimulatedOrderFaxes,
  signAndReturnSimulatedOrderFax,
  type SignedBackOrderFax,
  type SimulatedOrderFax,
} from '../../services/simulatedOrderFaxes';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import { formatDateTimeInLocalZone } from '../../utils/dateUtils';

const SIGN_BACK_FALLBACK = 'Please try again.';

/**
 * The outbound order faxes this stage kept instead of dialling out, and the
 * round trip back.
 *
 * The screen exists because a stage with no telephone line otherwise has no
 * way to prove the faxing path works at all. Reading the list shows the send
 * produced a real document with a real mark on it; signing one back pushes
 * that page through the genuine inbound pipeline, so the scan, the mark
 * lookup, the agency check and the order advancing are the production ones.
 *
 * Deliberately loud about never having sent anything. Somebody will eventually
 * open this on a stage holding copied production data and wonder whether a
 * physician really received an order, and the answer has to be on the screen.
 */
export const SimulatedOrderFaxesPanel: React.FC = () => {
  const { showError, showSuccess } = useStore(
    useShallow(state => ({
      showError: state.showError,
      showSuccess: state.showSuccess,
    }))
  );
  const openPdfTab = usePdfTab();

  const [faxes, setFaxes] = useState<SimulatedOrderFax[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  // A 404 is not a failure here. The whole route is absent wherever faxes are
  // genuinely sent, so it is the one answer that means "this stage has a fax
  // line" rather than "something broke".
  const [faxingIsLive, setFaxingIsLive] = useState(false);
  const [signingId, setSigningId] = useState<string | null>(null);
  const [lastReturn, setLastReturn] = useState<SignedBackOrderFax | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      setFaxes(await listSimulatedOrderFaxes());
      setLoadFailed(false);
      setFaxingIsLive(false);
    } catch (error) {
      setFaxes([]);
      if (getHttpStatus(error) === 404) {
        setFaxingIsLive(true);
        setLoadFailed(false);
      } else {
        console.error('Failed to load the simulated order faxes', error);
        setLoadFailed(true);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openPdf = (fax: SimulatedOrderFax) => {
    // Opened before the await, while the click is still the user's gesture.
    // After one it is gone and the blocker stops the tab.
    const tab = openPdfTab();
    if (!tab) return;
    void (async () => {
      try {
        tab.setBlob(await getSimulatedOrderFaxPdf(fax.id));
      } catch (error) {
        tab.close();
        console.error('Failed to open the stored fax', error);
        showError(
          'Could not open the stored fax',
          getBusinessErrorMessage(error) ?? SIGN_BACK_FALLBACK
        );
      }
    })();
  };

  const signAndReturn = async (fax: SimulatedOrderFax) => {
    setSigningId(fax.id);
    try {
      const outcome = await signAndReturnSimulatedOrderFax(fax.id);
      // Kept on screen rather than only toasted: the message is the finding,
      // and whether the mark matched is the thing being tested.
      setLastReturn(outcome);
      showSuccess('Signed and returned', outcome.message);
      await load();
    } catch (error) {
      console.error('Failed to sign the fax back', error);
      showError(
        'Could not sign this fax back',
        getBusinessErrorMessage(error) ?? SIGN_BACK_FALLBACK
      );
    } finally {
      setSigningId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Simulated Order Faxes</CardTitle>
        <CardDescription>
          Order faxes this stage stored instead of sending. Open one to see the
          document and its mark, then sign it back to run the return through the
          real inbound pipeline.
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        {/* Base styles are the mobile layout; the icon only stops shrinking
            once there is room for the sentence beside it. */}
        <Alert className='border-amber-300 bg-amber-50'>
          <AlertDescription className='flex items-start gap-2 text-sm text-amber-900'>
            <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600' />
            <span>
              <strong>Nothing here was faxed.</strong> This stage has no fax
              line, so every outbound order fax is stored instead of dialled. No
              physician received any of these.
            </span>
          </AlertDescription>
        </Alert>

        {lastReturn && (
          <Alert
            className={
              lastReturn.matched
                ? 'border-green-200 bg-green-50'
                : 'border-amber-300 bg-amber-50'
            }
          >
            <AlertDescription
              className={
                lastReturn.matched
                  ? 'flex items-start gap-2 text-sm text-green-900'
                  : 'flex items-start gap-2 text-sm text-amber-900'
              }
            >
              {lastReturn.matched ? (
                <CheckCircle2 className='h-4 w-4 shrink-0 text-green-600' />
              ) : (
                <Info className='h-4 w-4 shrink-0 text-amber-600' />
              )}
              <span>{lastReturn.message}</span>
            </AlertDescription>
          </Alert>
        )}

        {isLoading ? (
          <div className='flex justify-center py-10'>
            <Loader2 className='h-6 w-6 animate-spin' />
          </div>
        ) : faxingIsLive ? (
          <p className='py-10 text-center text-muted-foreground'>
            This stage sends faxes for real, so nothing is stored here.
          </p>
        ) : loadFailed ? (
          <div className='space-y-3 py-10 text-center'>
            <p className='text-destructive'>Could not load the stored faxes.</p>
            <Button variant='outline' size='sm' onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : faxes.length === 0 ? (
          <p className='py-10 text-center text-muted-foreground'>
            Nothing stored yet. Fax an order from the Order Manager and it will
            appear here.
          </p>
        ) : (
          <div className='overflow-x-auto'>
            {/* Wide enough that the columns keep their meaning; the container
                scrolls on a phone rather than wrapping every cell. */}
            <Table className='min-w-[900px]'>
              <TableHeader>
                <TableRow>
                  <TableHead>Stored</TableHead>
                  <TableHead>From</TableHead>
                  <TableHead>To</TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Pages</TableHead>
                  <TableHead>Returned</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {faxes.map(fax => (
                  <TableRow key={fax.id}>
                    <TableCell className='whitespace-nowrap'>
                      {formatDateTimeInLocalZone(fax.created_at)}
                    </TableCell>
                    <TableCell>
                      {/* Agency, then branch, then the line it would have
                          dialled out on: a multi-site agency has one number
                          per branch, so the agency alone does not say which. */}
                      <div>{fax.agency_name ?? 'Unknown agency'}</div>
                      {fax.clinic_name && (
                        <div className='text-xs text-muted-foreground'>
                          {fax.clinic_name}
                        </div>
                      )}
                      <div className='text-xs text-muted-foreground'>
                        {fax.from_fax_number ?? 'no fax line'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>{fax.physician_name ?? 'Unknown physician'}</div>
                      <div className='text-xs text-muted-foreground'>
                        {fax.to_fax_number}
                      </div>
                    </TableCell>
                    <TableCell>{fax.patient_label ?? 'Not recorded'}</TableCell>
                    <TableCell>{fax.page_count ?? '?'}</TableCell>
                    <TableCell>
                      {fax.returned_at ? (
                        <div className='space-y-1'>
                          <Badge
                            variant='secondary'
                            className='whitespace-nowrap'
                          >
                            signed back
                          </Badge>
                          <div className='whitespace-nowrap text-xs text-muted-foreground'>
                            {formatDateTimeInLocalZone(fax.returned_at)}
                          </div>
                        </div>
                      ) : (
                        <Badge variant='outline' className='whitespace-nowrap'>
                          awaiting signature
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className='text-right'>
                      <div className='flex items-center justify-end gap-2 whitespace-nowrap'>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => openPdf(fax)}
                        >
                          Open PDF
                        </Button>
                        {/* Absent rather than disabled once it has come back:
                            the return is what moves the order, and a second
                            one would be a second signature for the same
                            order. The server refuses it too. */}
                        {fax.returned_at === null && (
                          <Button
                            size='sm'
                            disabled={signingId !== null}
                            onClick={() => void signAndReturn(fax)}
                          >
                            {signingId === fax.id
                              ? 'Signing...'
                              : 'Sign and return'}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SimulatedOrderFaxesPanel;
